import React, { useMemo, useState } from "react";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceArea,
} from "recharts";
import { TrendingUp, TrendingDown, Minus, Search } from "lucide-react";

const slug = (name) => name.replace(/[^a-z0-9]+/gi, "-").toLowerCase();

const STATUS_DOT = {
  optimal: "#2A3B2C",
  attention: "#8F3A3A",
  borderline: "#D49A44",
};
const dotColor = (status) => STATUS_DOT[status] || "#9A8F7E";
const TOOLTIP_STYLE = { background: "#F9F6F0", border: "1px solid #D1C7B8", borderRadius: 8, fontSize: 12 };

export const MarkerTrendExplorer = ({ items, trends }) => {
  // series per marker, sorted by date
  const byMarker = useMemo(() => {
    const map = {};
    (items || []).forEach((b) => {
      (map[b.marker] = map[b.marker] || []).push({
        t: new Date(b.date).getTime(),
        date: new Date(b.date).toLocaleDateString(),
        value: b.value,
        unit: b.unit,
      });
    });
    Object.values(map).forEach((s) => s.sort((a, b) => a.t - b.t));
    return map;
  }, [items]);

  // zone / trend meta per marker (from trends endpoint)
  const meta = useMemo(() => {
    const m = {};
    (trends?.markers || []).forEach((t) => { m[t.marker] = t; });
    return m;
  }, [trends]);

  // marker list: attention → borderline → others, then alphabetical
  const markers = useMemo(() => {
    const names = Object.keys(byMarker);
    const rank = (n) => {
      const s = meta[n]?.zone?.status;
      return s === "attention" ? 0 : s === "borderline" ? 1 : s === "optimal" ? 2 : 3;
    };
    return names.sort((a, b) => rank(a) - rank(b) || a.localeCompare(b));
  }, [byMarker, meta]);

  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(null);
  const active = selected && byMarker[selected] ? selected : markers[0];

  const filtered = markers.filter((n) => n.toLowerCase().includes(query.toLowerCase()));

  if (!markers.length) {
    return (
      <div className="lg:col-span-2 bg-[#F1ECE4] border border-[#D1C7B8] rounded-3xl p-6 min-h-[400px] flex items-center justify-center text-[#5C6359]" data-testid="marker-trend-explorer">
        Record entries to explore each biomarker&apos;s trend.
      </div>
    );
  }

  const series = byMarker[active] || [];
  const info = meta[active];
  const zone = info?.zone;
  const latest = series[series.length - 1];
  const unit = latest?.unit || zone?.unit || "";
  const dir = info?.direction;
  const DirIcon = dir === "up" ? TrendingUp : dir === "down" ? TrendingDown : Minus;
  const dirTone = dir === "up" ? "text-[#C05C42]" : dir === "down" ? "text-[#2A3B2C]" : "text-[#5C6359]";
  const lineColor = dotColor(zone?.status);

  return (
    <div className="lg:col-span-2 bg-[#F1ECE4] border border-[#D1C7B8] rounded-3xl p-5 sm:p-6 min-h-[400px]" data-testid="marker-trend-explorer">
      <div className="grid grid-cols-1 sm:grid-cols-[190px_1fr] gap-5">
        {/* Marker list */}
        <div className="min-w-0">
          <p className="label-eyebrow mb-2">Biomarkers</p>
          <div className="relative mb-2">
            <Search className="w-3.5 h-3.5 text-[#9A8F7E] absolute left-2.5 top-1/2 -translate-y-1/2" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search…"
              data-testid="marker-search"
              className="w-full bg-[#F9F6F0] border border-[#D1C7B8] rounded-lg pl-8 pr-2 py-1.5 text-sm text-[#2D322B] focus:outline-none focus:border-[#9A8F7E]"
            />
          </div>
          <div className="space-y-1 max-h-[330px] overflow-y-auto pr-1" data-testid="marker-list">
            {filtered.map((name) => {
              const mi = meta[name];
              const last = byMarker[name][byMarker[name].length - 1];
              const isActive = name === active;
              return (
                <button
                  key={name}
                  type="button"
                  onClick={() => setSelected(name)}
                  data-testid={`marker-select-${slug(name)}`}
                  className={`w-full text-left rounded-lg px-2.5 py-2 transition-colors flex items-start gap-2 ${isActive ? "bg-[#2D322B] text-[#F9F6F0]" : "hover:bg-[#E7E0D4] text-[#2D322B]"}`}
                >
                  <span className="mt-1 w-2 h-2 rounded-full shrink-0" style={{ background: dotColor(mi?.zone?.status) }} />
                  <span className="min-w-0 flex-1">
                    <span className="block text-[11px] leading-tight break-words">{name}</span>
                    <span className={`block text-[10px] mt-0.5 ${isActive ? "text-[#D1C7B8]" : "text-[#5C6359]"}`}>
                      {last?.value} {last?.unit} · {byMarker[name].length} reading{byMarker[name].length === 1 ? "" : "s"}
                    </span>
                  </span>
                </button>
              );
            })}
            {filtered.length === 0 && <p className="text-xs text-[#9A8F7E] px-2 py-3">No match.</p>}
          </div>
        </div>

        {/* Selected marker chart */}
        <div className="min-w-0" data-testid={`marker-chart-${slug(active)}`}>
          <div className="flex items-start justify-between gap-3 flex-wrap" data-testid="marker-summary">
            <div>
              <p className="font-serif text-xl text-[#2D322B] leading-tight">{active}</p>
              <p className="mt-1">
                <span className="font-serif text-3xl text-[#2D322B]">{latest?.value ?? "—"}</span>
                <span className="text-sm text-[#5C6359] ml-1">{unit}</span>
              </p>
              {zone && (
                <p className="text-[11px] text-[#5C6359] mt-0.5">Optimal · {zone.optimal_low}–{zone.optimal_high} {zone.unit}</p>
              )}
            </div>
            <div className="text-right">
              {zone?.status && (
                <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full" style={{ background: `${lineColor}22`, color: lineColor }}>
                  {zone.zone || zone.status}
                </span>
              )}
              {info && info.delta_pct !== null && info.delta_pct !== undefined && (
                <div className={`mt-2 flex items-center justify-end gap-1 text-xs ${dirTone}`}>
                  <DirIcon className="w-3.5 h-3.5" />
                  <span>{info.delta_pct > 0 ? "+" : ""}{info.delta_pct}% vs prior</span>
                </div>
              )}
            </div>
          </div>

          <div className="mt-4">
            {series.length < 2 ? (
              <div className="h-[260px] flex items-center justify-center text-center text-sm text-[#5C6359] px-4" data-testid="marker-single-point">
                Only one reading so far. Add another lab to see this marker&apos;s trend line.
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <LineChart data={series} margin={{ top: 10, right: 12, bottom: 4, left: -8 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#D1C7B8" />
                  {zone && zone.optimal_low != null && zone.optimal_high != null && (
                    <ReferenceArea y1={zone.optimal_low} y2={zone.optimal_high} fill="#2A3B2C" fillOpacity={0.08} strokeOpacity={0} />
                  )}
                  <XAxis dataKey="date" stroke="#5C6359" fontSize={11} />
                  <YAxis stroke="#5C6359" fontSize={11} domain={["auto", "auto"]} />
                  <Tooltip contentStyle={TOOLTIP_STYLE} formatter={(v) => [`${v} ${unit}`, active]} />
                  <Line type="monotone" dataKey="value" stroke={lineColor} strokeWidth={2.5} dot={{ r: 3 }} activeDot={{ r: 5 }} />
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
